import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import restaurantLogo from '../assets/restaurant-logo.png';
import {
  QrCodeIcon,
  ChartBarIcon,
  UsersIcon,
  MapPinIcon,
  ListBulletIcon,
  PresentationChartLineIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightIcon,
  PlayIcon
} from '@heroicons/react/24/outline';

const LandingPage = () => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Check for pending payment redirect from previous implementation
  useEffect(() => {
    const pendingOrderId = localStorage.getItem('pending_order_id');
    const timestamp = localStorage.getItem('pending_order_timestamp');

    if (pendingOrderId) {
      const orderAge = Date.now() - parseInt(timestamp || '0');
      const fiveMinutes = 5 * 60 * 1000;

      if (orderAge < fiveMinutes) {
        localStorage.removeItem('pending_order_id');
        localStorage.removeItem('pending_restaurant_id');
        localStorage.removeItem('pending_table_id');
        localStorage.removeItem('pending_order_timestamp');
        window.location.href = `/payment-status?order_id=${pendingOrderId}`;
      } else {
        localStorage.removeItem('pending_order_id');
        localStorage.removeItem('pending_restaurant_id');
        localStorage.removeItem('pending_table_id');
        localStorage.removeItem('pending_order_timestamp');
      }
    }
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerCallback = (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('opacity-100', 'translate-y-0');
          entry.target.classList.remove('opacity-0', 'translate-y-10');
          observer.unobserve(entry.target);
        }
      });
    };

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    const elements = document.querySelectorAll('.reveal-on-scroll');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const features = [
    { icon: QrCodeIcon, title: "QR Menu", desc: "Digital menus with zero upfront hardware costs." },
    { icon: ChartBarIcon, title: "Live Order Dashboard", desc: "Monitor multi-table orders in real-time." },
    { icon: UsersIcon, title: "Auto Staff Assignment", desc: "Route orders to staff based on zones." },
    { icon: MapPinIcon, title: "Real-time Tracking", desc: "Kitchen to table transparency for diners." },
    { icon: ListBulletIcon, title: "Menu Management", desc: "Update prices and availability instantly." },
    { icon: PresentationChartLineIcon, title: "Analytics", desc: "Data to optimize your peak hours." }
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white font-sans overflow-x-hidden selection:bg-[#F5A623] selection:text-black">
      {/* NAV */}
      <nav className="fixed top-0 w-full z-50 bg-[#0D0D0D]/90 mt-0 py-4 px-6 border-b border-white/10 backdrop-blur-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo(0, 0)}>
            <img src={restaurantLogo} alt="Ordyrr Logo" className="h-8 md:h-10 w-auto object-contain drop-shadow-[0_0_8px_rgba(255,255,255,0.1)]" />
          </div>

          <div className="hidden md:flex items-center gap-10">
            <a href="features" className="text-sm font-medium text-gray-400 hover:text-white transition-colors">Features</a>
            <a href="#how-it-works" className="text-sm font-medium text-gray-400 hover:text-white transition-colors">How it works</a>
            <a href="pricing" className="text-sm font-medium text-gray-400 hover:text-white transition-colors">Pricing</a>
          </div>

          <div className="hidden md:block">
            <Link to="/auth" className="bg-[#F5A623] hover:bg-[#E0961E] text-black px-6 py-2.5 rounded-full text-sm font-bold transition-all transform hover:scale-105">
              Get Started
            </Link>
          </div>

          <button className="md:hidden text-white" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-[#111111] border-b border-white/10 py-6 px-6 flex flex-col gap-6">
            <a href="#features" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-white">Features</a>
            <a href="#how-it-works" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-white">How it works</a>
            <a href="#pricing" onClick={() => setIsMobileMenuOpen(false)} className="text-base font-medium text-white">Pricing</a>
            <Link to="/auth" onClick={() => setIsMobileMenuOpen(false)} className="bg-[#F5A623] text-black text-center px-6 py-3 rounded-full text-base font-bold w-full">
              Get Started
            </Link>
          </div>
        )}
      </nav>

      {/* HERO */}
      <section className="pt-32 pb-20 md:pt-48 md:pb-32 px-6 max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-16">
        <div className="flex-1 w-full lg:w-1/2 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-1000 ease-out">
          <div className="inline-block border border-white/20 bg-white/5 rounded-full px-4 py-1.5 mb-6 text-xs font-semibold tracking-wider text-gray-300">
            QR ORDERING REDEFINED
          </div>
          <h1 className="text-5xl md:text-7xl font-black leading-[1.05] tracking-tighter mb-6">
            Your restaurant.<br />
            <span className="text-white">Smarter.</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-lg mb-10 font-light leading-relaxed">
            Eliminate wait times, optimize table turns, and boost your bottom line with a premium contactless dining experience.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/auth" className="bg-[#F5A623] hover:bg-[#E0961E] text-black px-8 py-4 rounded-full text-base font-bold transition-all flex items-center justify-center gap-2 group">
              Start Free
              <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/menu/demo" className="bg-transparent border border-white/20 hover:border-white/50 text-white px-8 py-4 rounded-full text-base font-bold transition-all flex items-center justify-center gap-2 group">
              View Demo
              <PlayIcon className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" />
            </Link>
          </div>
        </div>

        <div className="flex-1 w-full lg:w-1/2 relative reveal-on-scroll opacity-0 translate-y-10 transition-all duration-1000 delay-200 ease-out">
          {/* Subtle Orange Glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-[#F5A623] blur-[150px] opacity-[0.15] rounded-full pointer-events-none"></div>

          {/* Dashboard Mockup */}
          <div className="relative bg-[#111111] border border-white/10 rounded-2xl shadow-2xl p-6 overflow-hidden">
            <div className="flex items-center justify-between mb-8 border-b border-white/10 pb-4">
              <div className="font-bold text-white text-lg">Live Orders</div>
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-[#1A1A1A] border border-white/5 p-4 rounded-xl shadow-lg">
                <div className="text-gray-500 text-xs font-semibold tracking-wider mb-1">TODAY'S REVENUE</div>
                <div className="text-3xl font-black text-white">₹42,500</div>
                <div className="text-green-500 text-xs font-medium mt-2">↑ 18% vs yesterday</div>
              </div>
              <div className="bg-[#1A1A1A] border border-white/5 p-4 rounded-xl shadow-lg">
                <div className="text-gray-500 text-xs font-semibold tracking-wider mb-1">ACTIVE ORDERS</div>
                <div className="text-3xl font-black text-[#F5A623]">24</div>
                <div className="text-gray-400 text-xs font-medium mt-2">4 ready for pickup</div>
              </div>
            </div>

            <div className="space-y-3">
              {[
                { table: '12', time: '2m ago', items: 3, total: '₹1,240' },
                { table: '04', time: '5m ago', items: 5, total: '₹2,800' },
                { table: '08', time: '8m ago', items: 2, total: '₹850' }
              ].map((order, i) => (
                <div key={i} className="flex items-center justify-between bg-[#0A0A0A] p-3 rounded-lg border border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded bg-[#F5A623]/20 text-[#F5A623] flex items-center justify-center font-bold text-sm">
                      T{order.table}
                    </div>
                    <div>
                      <div className="text-sm font-bold text-white">{order.items} Items</div>
                      <div className="text-xs text-gray-500">{order.time}</div>
                    </div>
                  </div>
                  <div className="font-bold text-white text-sm">{order.total}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF BAR */}
      <section className="border-y border-white/10 bg-[#0A0A0A] py-10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between md:justify-around gap-6 text-center reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 ease-out">
          <div className="text-gray-400 font-medium tracking-wide uppercase text-sm"><span className="text-white font-bold block md:inline text-lg">500+</span> Restaurants</div>
          <div className="hidden md:block w-px h-8 bg-white/10"></div>
          <div className="text-gray-400 font-medium tracking-wide uppercase text-sm"><span className="text-white font-bold block md:inline text-lg">50K+</span> Orders Daily</div>
          <div className="hidden md:block w-px h-8 bg-white/10"></div>
          <div className="text-gray-400 font-medium tracking-wide uppercase text-sm"><span className="text-white font-bold block md:inline text-lg">99.9%</span> Uptime</div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-24 md:py-32 px-6 max-w-7xl mx-auto">
        <div className="mb-16 md:mb-24 max-w-2xl reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 ease-out">
          <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-4 text-white">
            Everything you need. <br /><span className="text-gray-500">Nothing you don't.</span>
          </h2>
          <p className="text-gray-400 text-lg font-light">
            Designed for speed and reliability, empowering your staff rather than replacing them.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {features.map((feature, i) => (
            <div key={i} className="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 ease-out" style={{ transitionDelay: `${i * 100}ms` }}>
              <feature.icon className="w-8 h-8 text-[#F5A623] mb-6" />
              <h3 className="text-xl font-bold text-white mb-3 tracking-tight">{feature.title}</h3>
              <p className="text-gray-400 text-sm md:text-base font-light leading-relaxed">{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-24 md:py-32 bg-[#111111] px-6 border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-20 tracking-tight reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 ease-out">How It Works</h2>

          <div className="relative">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-[40px] left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>

            <div className="grid md:grid-cols-3 gap-12 text-center">
              <div className="relative text-center reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 ease-out">
                <div className="w-20 h-20 mx-auto bg-[#0D0D0D] border border-white/10 rounded-full flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(245,166,35,0.1)] relative z-10 text-white font-black text-xl">1</div>
                <h3 className="text-xl font-bold mb-2 tracking-tight">Setup QR</h3>
                <p className="text-gray-400 text-sm font-light">Generate unique codes for each table. No app download required for diners.</p>
              </div>
              <div className="relative text-center reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 ease-out" style={{ transitionDelay: '200ms' }}>
                <div className="w-20 h-20 mx-auto bg-[#0D0D0D] border border-[#F5A623]/50 rounded-full flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(245,166,35,0.2)] relative z-10 text-[#F5A623] font-black text-xl">2</div>
                <h3 className="text-xl font-bold mb-2 tracking-tight">Customer Orders</h3>
                <p className="text-gray-400 text-sm font-light">Guests browse the visual menu, select items, and pay seamlessly from their phone.</p>
              </div>
              <div className="relative text-center reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 ease-out" style={{ transitionDelay: '400ms' }}>
                <div className="w-20 h-20 mx-auto bg-[#0D0D0D] border border-white/10 rounded-full flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(255,255,255,0.05)] relative z-10 text-white font-black text-xl">3</div>
                <h3 className="text-xl font-bold mb-2 tracking-tight">Staff Notified</h3>
                <p className="text-gray-400 text-sm font-light">Kitchen and waiters receive real-time alerts. Execution begins.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* METRICS/PROOF */}
      <section id="metrics" className="py-24 md:py-32 bg-[#0D0D0D] px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-16 md:gap-8 text-center divide-y md:divide-y-0 md:divide-x divide-white/5">
            <div className="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 ease-out py-2 md:py-0">
              <div className="text-5xl md:text-7xl font-black text-[#F5A623] mb-4 tracking-tighter">40%</div>
              <div className="text-lg font-medium text-white tracking-wide">Revenue Boost</div>
              <div className="text-sm text-gray-500 mt-2 font-light">Average increase in order size</div>
            </div>
            <div className="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 ease-out py-6 md:py-0" style={{ transitionDelay: '200ms' }}>
              <div className="text-5xl md:text-7xl font-black text-[#F5A623] mb-4 tracking-tighter">60%</div>
              <div className="text-lg font-medium text-white tracking-wide">Less Wait Time</div>
              <div className="text-sm text-gray-500 mt-2 font-light">Faster table turnaround</div>
            </div>
            <div className="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 ease-out py-6 md:py-0" style={{ transitionDelay: '400ms' }}>
              <div className="text-5xl md:text-7xl font-black text-[#F5A623] mb-4 tracking-tighter">2m</div>
              <div className="text-lg font-medium text-white tracking-wide">Setup Process</div>
              <div className="text-sm text-gray-500 mt-2 font-light">Quick deployment</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-32 px-6 relative overflow-hidden bg-[#111111] border-t border-white/5">
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
        <div className="max-w-4xl mx-auto text-center relative z-10 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 ease-out">
          <h2 className="text-4xl md:text-6xl font-black tracking-tighter text-white mb-6">
            Ready to upgrade your<br />restaurant experience?
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-xl mx-auto font-light">
            Join the restaurants taking control of their operations and data with Ordyrr.
          </p>
          <Link to="/auth" className="inline-block bg-[#F5A623] hover:bg-[#E0961E] text-black px-10 py-5 rounded-full text-lg font-bold transition-all transform hover:scale-105 shadow-[0_0_40px_rgba(245,166,35,0.3)] hover:shadow-[0_0_60px_rgba(245,166,35,0.4)]">
            Start Free Trial
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0A0A0A] pt-20 pb-10 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-10">
          <div>
            <div className="mb-4">
              <img src={restaurantLogo} alt="Ordyrr Logo" className="h-8 md:h-10 w-auto object-contain drop-shadow-[0_0_8px_rgba(255,255,255,0.1)]" />
            </div>
            <p className="text-sm text-gray-500 max-w-xs font-light">
              Premium QR ordering and management platform for modern restaurants.
            </p>
          </div>

          <div className="flex gap-8 md:gap-16">
            <div className="flex flex-col gap-3">
              <a href="#features" className="text-sm text-gray-400 hover:text-white transition-colors">Features</a>
              <a href="#pricing" className="text-sm text-gray-400 hover:text-white transition-colors">Pricing</a>
            </div>
            <div className="flex flex-col gap-3">
              <Link to="/contact" className="text-sm text-gray-400 hover:text-white transition-colors">Contact</Link>
              <Link to="/help" className="text-sm text-gray-400 hover:text-white transition-colors">Support</Link>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-gray-600">
          <p>&copy; {new Date().getFullYear()} Ordyrr. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-gray-300 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-gray-300 transition-colors">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
